/**
 * Dispatch registration fields (Screen 7).
 *
 * Three naming layers (do not mix them in POST bodies):
 * - TS form state: `dispatch*` camelCase (e.g. dispatchFacilityClassification)
 * - Buxus API POST (`JsonPropertyName` on BuxusAccountSetupCrmContact): `dispatch_*` with first
 *   character lowercased vs Zoho (e.g. dispatch_Name_of_Superior)
 * - Zoho CRM API (`JsonProperty` on ZohoCrmContact): `Dispatch_*` (e.g. Dispatch_Name_of_Superior)
 *
 * Option values use ASCII hyphens only (never en/em dashes).
 */

export const DISPATCH_FACILITY_CLASSIFICATION_OPTIONS = ["Primary", "Secondary"] as const;

export const DISPATCH_DISCIPLINES_SERVED_OPTIONS = [
  "Animal Services",
  "Emergency Services",
  "EMS - County",
  "EMS - Municipal",
  "EMS - Private/3rd Party",
  "Fire - County",
  "Fire - Federal",
  "Fire - Municipal",
  "Fire - Private",
  "Fire - State",
  "Game Warden",
  "Law - County",
  "Law - Federal",
  "Law - Municipal",
  "Law - State",
  "Other - County",
  "Other - Federal",
  "Other - Municipal",
  "Other - State",
  "Special Districts (River Authority, Water District, etc.)",
  "Tax District",
  "Tribal",
] as const;

export const DISPATCH_MAIN_AREA_OF_RESPONSIBILITY_OPTIONS = [
  "Urban",
  "Rural",
  "Suburban",
  "Exurban",
  "Rural Urban",
  "Frontier/Remote",
  "Vacation",
] as const;

export const DISPATCH_CAD_SYSTEM_OPTIONS = [
  "Crimes",
  "Central Square (majority)",
  "Mark 43",
  "Motorola",
  "Spillman",
  "Tyler",
  "No CAD system",
  "Other",
] as const;

export const DISPATCH_PROGRAMS_USED_OPTIONS = [
  "Active911",
  "Buxus",
  "Convey911",
  "First Due",
  "I Am Responding",
  "Prepared",
  "Rapid Deploy",
  "RapidSOS",
] as const;

export const DISPATCH_RADIO_SYSTEM_OPTIONS = [
  "700 / 800 Trunk",
  "VHF - Digital",
  "VHF - Analog",
  "UHF",
  "CB",
  "Other",
] as const;

/**
 * Shared hazmat/topic options reused across the revised emergency-topic
 * multi-selects. Keep CRM option values coordinated with this list.
 */
export const DISPATCH_SHARED_HAZMAT_TOPIC_OPTIONS = [
  "Natural Gas",
  "Hazardous Liquids",
  "Highly Volatile Liquids",
  "Chemicals",
  "Carbon Dioxide",
] as const;

/**
 * @deprecated Prefer field-specific lists below. Alias of shared hazmat topics.
 */
export const DISPATCH_EMERGENCY_TOPIC_OPTIONS = DISPATCH_SHARED_HAZMAT_TOPIC_OPTIONS;

/**
 * `Dispatch_Training_Covers_Emergency_Topics`
 * UI: Does your basic telecommunicator training include the following?
 */
export const DISPATCH_BASIC_TELECOMMUNICATOR_TRAINING_OPTIONS = [
  "Pipelines",
  "Local Distribution Systems",
  ...DISPATCH_SHARED_HAZMAT_TOPIC_OPTIONS,
] as const;

/**
 * `Dispatch_Has_Access_To_Emergency_Topics`
 * UI: Do you have access to the following specific training?
 * Same options as basic telecommunicator training.
 */
export const DISPATCH_ACCESS_TO_SPECIFIC_TRAINING_OPTIONS =
  DISPATCH_BASIC_TELECOMMUNICATOR_TRAINING_OPTIONS;

/**
 * `Dispatch_SOP_SOG_For_Emergency_Topics`
 * UI: Does your agency have a specific SOP/SOG for the following?
 */
export const DISPATCH_SOP_SOG_FOR_TOPICS_OPTIONS = [
  "Pipeline Emergencies",
  ...DISPATCH_SHARED_HAZMAT_TOPIC_OPTIONS,
] as const;

/**
 * `Dispatch_Completed_Courses_On_Emergency_Topics`
 * UI: Have you completed a training class on any of the following?
 */
export const DISPATCH_COMPLETED_TRAINING_CLASS_OPTIONS = [
  "Pipeline Preparedness and Response",
  ...DISPATCH_SHARED_HAZMAT_TOPIC_OPTIONS,
] as const;

export const DISPATCH_CONTINUING_EDUCATION_REQUIRED_OPTIONS = ["Yes", "No", "Don't Know"] as const;

export const DISPATCH_STATE_REQUIRE_CERTIFICATION_OPTIONS = [
  "All",
  "Only for law enforcement",
  "No",
  "Not sure",
  "Other",
] as const;

export const DISPATCH_PUBLIC_NOTIFICATION_SYSTEM_OPTIONS = [
  "CodeRED",
  "Hyper-Reach",
  "Everbridge",
  "AlertMedia",
  "Rave Alert",
  "Regroup",
  "Genasys",
  "Other",
] as const;

/** Buxus API POST body keys (`JsonPropertyName` on BuxusAccountSetupCrmContact). */
export const DISPATCH_API_KEYS = {
  facilityClassification: "dispatch_Facility_Classification",
  disciplinesServedByAgency: "dispatch_Disciplines_Served_By_Agency",
  mainAreaOfResponsibility: "dispatch_Main_Area_Of_Responsibility",
  estPopulationOfResponsibilityArea: "dispatch_Est_Population_Of_Responsibility_Area",
  computerAidedDispatchSystemUsed: "dispatch_Computer_Aided_Dispatch_System_Used",
  computerAidedDispatchSystemUsedOther: "dispatch_Computer_Aided_Dispatch_System_Used_Other",
  canDetermineCallerLatLongGis: "dispatch_Can_Determine_Caller_Lat_Long_GIS",
  programsUsedByAgency: "dispatch_Programs_Used_By_Agency",
  radioSystemsUsedByAgency: "dispatch_Radio_Systems_Used_By_Agency",
  radioSystemsUsedByAgencyOther: "dispatch_Radio_Systems_Used_By_Agency_Other",
  publicNotificationSystemUsed: "dispatch_Public_Notification_System_Used",
  publicNotificationSystemUsedOther: "dispatch_Public_Notification_System_Used_Other",
  isContinuingEducationRequired: "dispatch_Is_Continuing_Education_Required",
  hasAccessToEmergencyTopics: "dispatch_Has_Access_To_Emergency_Topics",
  sopSogForEmergencyTopics: "dispatch_SOP_SOG_For_Emergency_Topics",
  trainingCoversEmergencyTopics: "dispatch_Training_Covers_Emergency_Topics",
  completedCoursesOnEmergencyTopics: "dispatch_Completed_Courses_On_Emergency_Topics",
  isRadioSystemEncrypted: "dispatch_Is_Radio_System_Encrypted",
  isRadioSystemP25Compliant: "dispatch_Is_Radio_System_P25_Compliant",
  doesStateRequireCertification: "dispatch_Does_State_Require_Certification",
  doesStateRequireCertificationOther: "dispatch_Does_State_Require_Certification_Other",
  /** Unused this pass — do not write from registration. */
  doesTrainingCoverPipelineEmergencies: "dispatch_Does_Training_Cover_Pipeline_Emergencies",
  /** Unused this pass — do not write from registration. */
  hasAccessToPipelineEmergencyTraining: "dispatch_Has_Access_To_Pipeline_Emergency_Training",
  /** Unused this pass — do not write from registration. */
  hasSopSogForPipelineEmergencies: "dispatch_Has_SOP_SOG_For_Pipeline_Emergencies",
  /** Unused this pass — do not write from registration. */
  hasCompletedPipelineTrainingClass: "dispatch_Has_Completed_Pipeline_Training_Class",
} as const;

/** Zoho CRM API field names (`JsonProperty` on ZohoCrmContact). Backend maps API keys to these. */
export const DISPATCH_ZOHO_API_KEYS = {
  facilityClassification: "Dispatch_Facility_Classification",
  disciplinesServedByAgency: "Dispatch_Disciplines_Served_By_Agency",
  mainAreaOfResponsibility: "Dispatch_Main_Area_Of_Responsibility",
  estPopulationOfResponsibilityArea: "Dispatch_Est_Population_Of_Responsibility_Area",
  computerAidedDispatchSystemUsed: "Dispatch_Computer_Aided_Dispatch_System_Used",
  computerAidedDispatchSystemUsedOther: "Dispatch_Computer_Aided_Dispatch_System_Used_Other",
  canDetermineCallerLatLongGis: "Dispatch_Can_Determine_Caller_Lat_Long_GIS",
  programsUsedByAgency: "Dispatch_Programs_Used_By_Agency",
  radioSystemsUsedByAgency: "Dispatch_Radio_Systems_Used_By_Agency",
  radioSystemsUsedByAgencyOther: "Dispatch_Radio_Systems_Used_By_Agency_Other",
  publicNotificationSystemUsed: "Dispatch_Public_Notification_System_Used",
  publicNotificationSystemUsedOther: "Dispatch_Public_Notification_System_Used_Other",
  isContinuingEducationRequired: "Dispatch_Is_Continuing_Education_Required",
  hasAccessToEmergencyTopics: "Dispatch_Has_Access_To_Emergency_Topics",
  sopSogForEmergencyTopics: "Dispatch_SOP_SOG_For_Emergency_Topics",
  trainingCoversEmergencyTopics: "Dispatch_Training_Covers_Emergency_Topics",
  completedCoursesOnEmergencyTopics: "Dispatch_Completed_Courses_On_Emergency_Topics",
  isRadioSystemEncrypted: "Dispatch_Is_Radio_System_Encrypted",
  isRadioSystemP25Compliant: "Dispatch_Is_Radio_System_P25_Compliant",
  doesStateRequireCertification: "Dispatch_Does_State_Require_Certification",
  doesStateRequireCertificationOther: "Dispatch_Does_State_Require_Certification_Other",
  doesTrainingCoverPipelineEmergencies: "Dispatch_Does_Training_Cover_Pipeline_Emergencies",
  hasAccessToPipelineEmergencyTraining: "Dispatch_Has_Access_To_Pipeline_Emergency_Training",
  hasSopSogForPipelineEmergencies: "Dispatch_Has_SOP_SOG_For_Pipeline_Emergencies",
  hasCompletedPipelineTrainingClass: "Dispatch_Has_Completed_Pipeline_Training_Class",
} as const;

/** @deprecated Use DISPATCH_API_KEYS for POST payloads. */
export const DISPATCH_CRM_API_KEYS = DISPATCH_API_KEYS;
